import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../lib/api";

/**
 * useSystemMode — reads/writes the ONE global operating mode
 * (core/mode_policy.py's `system_mode` singleton). Persisted, displayed,
 * and effective mode are the exact same document; this hook polls it so
 * the Floor never shows a stale value from a different session/device.
 *
 * P0 (2026-10-16): this REPLACES the old per-user `away_mode` toggle —
 * Away Mode is one switch for the whole desk now, not a personal
 * preference. "Whomever is in control of the dispatch is responsible for
 * how this behaves."
 */
export function useSystemMode({ pollMs = 15000 } = {}) {
  const [state, setState] = useState({ mode: null, degraded: false, loaded: false, policies: {} });

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/system-mode");
      setState({ mode: data.mode, degraded: !!data.degraded, loaded: true, policies: data.policies || {} });
    } catch {
      setState(s => ({ ...s, loaded: true }));
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs]);

  const setMode = useCallback(async (mode) => {
    await apiClient.put("/system-mode", { mode });
    await refresh();
  }, [refresh]);

  return { ...state, setMode, refresh, on: state.mode === "away" };
}
